/**
 * PortalInicio — pantalla de entrada del Portal de Clientes.
 * Indicadores (proyectos y cotizaciones), aviso de cotizaciones por revisar y
 * últimas novedades. Solo presenta datos que ya entregan las RPC del portal.
 */
import { FolderOpen, FileText, Activity, CheckCircle2, AlertCircle, ChevronRight, MessageSquare, Sparkles } from "lucide-react";
import { resumenInicio, novedadesInicio } from "../../lib/cotizacionesPortal";
import { fmtRelativo } from "../../lib/formato";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0", accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8", ok:"#10b981", okL:"#d1fae5",
  warn:"#f59e0b", warnL:"#fef3c7", purple:"#6366f1", purpleL:"#eef2ff",
};
const card = { background:C.white, border:`1px solid ${C.border}`, borderRadius:12, boxShadow:"0 1px 3px #0000000a" };

function Indicador({ icono, valor, etiqueta, color, fondo, onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label={`${etiqueta}: ${valor}`}
      style={{...card, padding:"16px 18px", textAlign:"left", cursor:"pointer", fontFamily:"'DM Sans',sans-serif", color:C.text}}>
      <div style={{width:32, height:32, borderRadius:9, background:fondo, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:10}}>
        {icono}
      </div>
      <div style={{fontSize:28, fontWeight:800, lineHeight:1, color}}>{valor}</div>
      <div style={{fontSize:12, color:C.sub, marginTop:5, fontWeight:600}}>{etiqueta}</div>
    </button>
  );
}

export default function PortalInicio({ nombre, proyectos = [], cotizaciones = [], cargando = false, onIrProyectos, onIrCotizaciones, onAbrirProyecto }) {
  const r = resumenInicio(proyectos, cotizaciones);
  const pendientes = cotizaciones.filter(c => c.puede_responder);
  const novedades = novedadesInicio(proyectos, cotizaciones);

  return (
    <div>
      {pendientes.length > 0 && (
        <div role="status" style={{...card, padding:"14px 16px", marginBottom:16, background:C.warnL, border:`1px solid ${C.warn}60`,
          display:"flex", gap:12, alignItems:"center", flexWrap:"wrap"}}>
          <AlertCircle size={20} color={C.warn} aria-hidden="true"/>
          <div style={{flex:1, minWidth:200}}>
            <div style={{fontWeight:800, color:"#92400e", fontSize:14}}>
              {pendientes.length === 1 ? "Tienes 1 cotización pendiente de revisión" : `Tienes ${pendientes.length} cotizaciones pendientes de revisión`}
            </div>
            <div style={{fontSize:12, color:"#92400e", marginTop:2}}>
              {pendientes.length === 1 ? `${pendientes[0].id} · puedes aprobarla, rechazarla o pedir cambios.` : "Revísalas y autoriza los trabajos desde tu cuenta."}
            </div>
          </div>
          <button type="button" onClick={() => onIrCotizaciones(pendientes.length === 1 ? pendientes[0].id : null)}
            style={{padding:"9px 16px", borderRadius:8, border:"none", background:C.warn, color:"#fff", fontWeight:700, fontSize:13, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
            Revisar ahora
          </button>
        </div>
      )}

      <div style={{marginBottom:14, fontSize:15, color:C.sub}}>
        Hola, <strong style={{color:C.text}}>{nombre || "bienvenido"}</strong>. Este es el resumen de tu cuenta.
      </div>

      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))", gap:12, marginBottom:20}}>
        <Indicador icono={<FolderOpen size={16} color={C.accent}/>} valor={r.proyectos} etiqueta="Proyectos asignados" color={C.text} fondo={C.accentL} onClick={onIrProyectos}/>
        <Indicador icono={<Activity size={16} color={C.purple}/>} valor={r.enEjecucion} etiqueta="Proyectos en ejecución" color={C.text} fondo={C.purpleL} onClick={onIrProyectos}/>
        <Indicador icono={<FileText size={16} color={C.warn}/>} valor={r.pendientes} etiqueta="Cotizaciones pendientes" color={r.pendientes ? "#b45309" : C.text} fondo={C.warnL} onClick={() => onIrCotizaciones(null)}/>
        <Indicador icono={<CheckCircle2 size={16} color={C.ok}/>} valor={r.aprobadas} etiqueta="Cotizaciones aprobadas" color={C.text} fondo={C.okL} onClick={() => onIrCotizaciones(null)}/>
      </div>

      <section aria-labelledby="novedades-titulo" style={{...card, padding:"16px 18px"}}>
        <h3 id="novedades-titulo" style={{margin:"0 0 10px", fontSize:14, fontWeight:700, display:"flex", alignItems:"center", gap:6}}>
          <Sparkles size={14} color={C.accent} aria-hidden="true"/> Últimas novedades
        </h3>
        {cargando && !novedades.length ? <div style={{fontSize:13, color:C.sub}}>Cargando…</div>
          : !novedades.length ? <div style={{fontSize:13, color:C.sub}}>Sin novedades por ahora. Te avisaremos aquí cuando haya comentarios nuevos o una cotización por revisar.</div>
          : (
            <ul style={{listStyle:"none", margin:0, padding:0, display:"grid", gap:2}}>
              {novedades.map(n => (
                <li key={`${n.tipo}-${n.id}`}>
                  <button type="button" onClick={() => n.tipo === "comentarios" ? onAbrirProyecto?.(n.id) : onIrCotizaciones(n.id)}
                    style={{width:"100%", display:"flex", alignItems:"center", gap:10, padding:"9px 6px", background:"none", border:"none", borderRadius:8,
                      cursor:"pointer", textAlign:"left", fontFamily:"'DM Sans',sans-serif", color:C.text, fontSize:13}}>
                    {n.tipo === "comentarios" ? <MessageSquare size={14} color={C.accent} aria-hidden="true"/> : <FileText size={14} color={C.warn} aria-hidden="true"/>}
                    <span style={{flex:1}}>{n.texto}</span>
                    {n.fecha && <span style={{fontSize:11, color:C.light}}>{fmtRelativo(n.fecha)}</span>}
                    <ChevronRight size={14} color={C.light} aria-hidden="true"/>
                  </button>
                </li>
              ))}
            </ul>
          )}
      </section>
    </div>
  );
}
